import React, { useEffect, useCallback, useState } from 'react';

import { Container } from '@/src/ui';
import { apiRequest } from '@/src/services';
import AttemptDetailsFilters from './components/AttemptDetailsFilters';
import type { FilterState } from './models/filterDTOs';
import type { StudentExamAttemptsDTO } from './models/attemptDetailsDTOs';
import AttemptDetailsTable from './components/AttemptDetailsTable';
import { useAuth } from '@/src/app/context/useAuth';

const AttemptDetailsPage: React.FC = () => {
    document.title = 'Attempt Details';

    const [loaded, setLoaded] = useState(false);
    const [error, setError] = useState('');
    const [data, setData] = useState<Array<StudentExamAttemptsDTO>>([]);
    const [hasSubmittedFilters, setHasSubmittedFilters] = useState(false);
    const { user } = useAuth();
    const userId = user?.id || 'nouser';
    const UNIVERSAL_FILTER_KEY = `universal-filters-${userId}`;

    const [filterState, setFilterState] = useState<FilterState>(() => {
        try {
            const saved = localStorage.getItem(UNIVERSAL_FILTER_KEY);
            if (saved) {
                const parsed = JSON.parse(saved);
                return {
                    districtId: parsed.districtId || 0,
                    schoolId: parsed.schoolId || 0,
                    gradeLevelMasterId: parsed.gradeLevelMasterId || 0,
                    classId: parsed.classId || 0,
                    examId: 0,
                    studentId: 0,
                };
            }
        } catch {
            // intentionally empty
        }
        return {
            districtId: 0,
            schoolId: 0,
            gradeLevelMasterId: 0,
            classId: 0,
            examId: 0,
            studentId: 0,
        };
    });

    const refreshTableData = useCallback(
        async (filters: FilterState = filterState) => {
            setHasSubmittedFilters(true);
            setError('');
            setLoaded(false);

            let requestUrl = `assessments/studentExamAttempts/attemptDetails?`;
            requestUrl += `districtId=${filters.districtId}`;
            requestUrl += `&schoolId=${filters.schoolId}`;
            requestUrl += `&classId=${filters.classId}`;
            requestUrl += `&gradeLevelMasterId=${filters.gradeLevelMasterId}`;
            requestUrl += `&examId=${filters.examId}`;
            requestUrl += `&studentId=${filters.studentId}`;

            try {
                const res = await apiRequest<StudentExamAttemptsDTO[]>('GET', requestUrl);
                setData(res.data);
            } catch {
                setError('Failed to fetch exam summary data');
            } finally {
                setLoaded(true);
            }
        },
        [filterState]
    );

    // Save universal filter memory
    useEffect(() => {
        const universalFilters = {
            gradeLevelMasterId: filterState.gradeLevelMasterId,
            districtId: filterState.districtId,
            schoolId: filterState.schoolId,
            classId: filterState.classId,
        };
        try {
            localStorage.setItem(UNIVERSAL_FILTER_KEY, JSON.stringify(universalFilters));
        } catch {
            // intentionally empty
        }
    }, [filterState, UNIVERSAL_FILTER_KEY]);

    return (
        <Container>
            <h1 className="page-header"> Attempt Details </h1>
            <br />

            <AttemptDetailsFilters
                refreshTableData={refreshTableData}
                filterState={filterState}
                setFilterState={setFilterState}
            />

            {hasSubmittedFilters && (
                <AttemptDetailsTable
                    data={data}
                    isLoading={!loaded}
                    error={error}
                    refreshTableData={refreshTableData}
                />
            )}
        </Container>
    );
};

export default AttemptDetailsPage;
